import { RootState } from 'app/store';
import { LoadConfigAsync } from 'global/Application/dataActions';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { EMergeVMStatus, IEntitiesContainerVM, IGroupConfigurationComparisonVM } from 'global/Compare/ComparisonViewModelGenerator';
import { IGCDataLight } from 'global/Reducer/dataSlice';
import { bcbs_groupconfigurationsnapshot as XRM_bcbs_groupconfigurationsnapshot } from 'model/CRM';
import { IComment, ICommentRouteRef } from 'model/FlexibleAttributeModel';

// Types and Interfaces
// ============================================================================
export interface ICommentInfo {
    comment: IComment | undefined,
    label: string,
    onOrigin: boolean,
    onTarget: boolean,
}

export type TSetCommentInfoDelegate = (
    comment: IComment | undefined,
    label: string,
    onOrigin: boolean,
    onTarget: boolean,
) => void;

export interface ICompareRowCommentTarget {
    tabId: string;
    sectionId: string;
    fieldId: string;
}

export enum EUnderwritingConfirmationDialogState {
    Hidden,
    Approve,
    Corrections,
    Save,
}

export interface IPendingMergeValidationDetails {
    total: number;
    levelCounts: {
        entity: number;
        field: number;
        attributeSet: number;
        attributeSetField: number;
        nonStandard: number;
        nonStandardField: number;
    };
    sampleLabels: string[];
}

export interface IPendingMergeValidationError {
    summary: string;
    levelItems: string[];
    samples: string[];
}

// Merge validation
// ============================================================================
export const getAllEntityContainers = (model: IGroupConfigurationComparisonVM): IEntitiesContainerVM[] => {
    return [
        model.groupConfiguration,
        model.nonStandardTemplate,
        model.plan,
        model.benefit,
        model.feesAndPremium,
        model.feesSummary,
        model.groupNumber,
        model.cesNumber,
        model.department,
        model.stopLossSummary,
        model.stopLoss,
        model.accountsReceivable,
    ];
};

export const getPendingMergeValidationDetails = (model: IGroupConfigurationComparisonVM): IPendingMergeValidationDetails => {
    const details: IPendingMergeValidationDetails = {
        total: 0,
        levelCounts: {
            entity: 0,
            field: 0,
            attributeSet: 0,
            attributeSetField: 0,
            nonStandard: 0,
            nonStandardField: 0,
        },
        sampleLabels: [],
    };

    const addSample = (label: string) => {
        if (details.sampleLabels.length >= 6) {
            return;
        }

        details.sampleLabels.push(label);
    };

    getAllEntityContainers(model).forEach((container) => {
        container.entities.forEach((entity) => {
            if (entity.changed && entity.mergeAction === EMergeVMStatus.Pend) {
                details.total += 1;
                details.levelCounts.entity += 1;
                addSample(`Entity: ${entity.friendlyName ?? entity.name}`);
            }

            entity.tabsVM.forEach((tab) => {
                tab.sectionsVM.forEach((section) => {
                    section.attributesVM.forEach((fieldRow) => {
                        // TODO fix for mergedField
                        if (fieldRow.changed && fieldRow.mergeAction === EMergeVMStatus.Pend) {
                            details.total += 1;
                            details.levelCounts.field += 1;
                            addSample(`Field: ${fieldRow.attributeDefinition.bcbs_config_attribute.bcbs_label}`);
                        }
                    });

                    section.attributeSetsVM.forEach((attributeSetGroup) => {
                        attributeSetGroup.attributeSets.forEach((attributeSet) => {
                            if (attributeSet.changed && attributeSet.mergeAction === EMergeVMStatus.Pend) {
                                details.total += 1;
                                details.levelCounts.attributeSet += 1;
                                addSample(`Attribute Set: ${attributeSet.definition.bcbs_config_attribute.bcbs_label} (${attributeSet.name})`);
                            }

                            attributeSet.attributesVM.forEach((attributeRow) => {
                                if (attributeRow.changed && attributeRow.mergeAction === EMergeVMStatus.Pend) {
                                    details.total += 1;
                                    details.levelCounts.attributeSetField += 1;
                                    addSample(`Attribute Set Field: ${attributeRow.attributeDefinition.bcbs_config_attribute.bcbs_label}`);
                                }
                            });
                        });
                    });

                    section.nonStandardsVM.forEach((nonStandardVM) => {
                        if (nonStandardVM.changed && nonStandardVM.mergeAction === EMergeVMStatus.Pend) {
                            details.total += 1;
                            details.levelCounts.nonStandard += 1;
                            addSample(`Non-Standard: ${nonStandardVM.name}`);
                        }

                        nonStandardVM.attributesVM.forEach((attributeRow) => {
                            if (attributeRow.changed && attributeRow.mergeAction === EMergeVMStatus.Pend) {
                                details.total += 1;
                                details.levelCounts.nonStandardField += 1;
                                addSample(`Non-Standard Field: ${attributeRow.attributeDefinition.bcbs_config_attribute.bcbs_label}`);
                            }
                        });
                    });
                });
            });
        });
    });

    return details;
};

export const formatPendingLevelSummary = (levelCounts: IPendingMergeValidationDetails['levelCounts']): string => {
    const parts: string[] = [];

    if (levelCounts.entity > 0) parts.push(`Entities (${levelCounts.entity})`);
    if (levelCounts.field > 0) parts.push(`Fields (${levelCounts.field})`);
    if (levelCounts.attributeSet > 0) parts.push(`Attribute Sets (${levelCounts.attributeSet})`);
    if (levelCounts.attributeSetField > 0) parts.push(`Attribute Set Fields (${levelCounts.attributeSetField})`);
    if (levelCounts.nonStandard > 0) parts.push(`Non-Standards (${levelCounts.nonStandard})`);
    if (levelCounts.nonStandardField > 0) parts.push(`Non-Standard Fields (${levelCounts.nonStandardField})`);

    return parts.join(', ');
};

export const getPendingLevelItems = (levelCounts: IPendingMergeValidationDetails['levelCounts']): string[] => {
    const items: string[] = [];

    if (levelCounts.entity > 0) items.push(`Entities (${levelCounts.entity})`);
    if (levelCounts.field > 0) items.push(`Fields (${levelCounts.field})`);
    if (levelCounts.attributeSet > 0) items.push(`Attribute Sets (${levelCounts.attributeSet})`);
    if (levelCounts.attributeSetField > 0) items.push(`Attribute Set Fields (${levelCounts.attributeSetField})`);
    if (levelCounts.nonStandard > 0) items.push(`Non-Standards (${levelCounts.nonStandard})`);
    if (levelCounts.nonStandardField > 0) items.push(`Non-Standard Fields (${levelCounts.nonStandardField})`);

    return items;
};

export const countPendingMergeSelections = (model: IGroupConfigurationComparisonVM): number => {
    return getPendingMergeValidationDetails(model).total;
};

// Data loading
// ============================================================================
export const LoadGroupConfigurationDataAsync = async (
    appDataSource: AppDataSource,
    state: RootState,
    bcbs_groupconfigurationsnapshotid: string,
    isMergeApp: boolean | undefined,
) => {

    // Snapshot:
    // ----------------------------------------
    // When merging, check whether the latest published release differs from the currently
    // loaded config. If it does, reload the config against the latest release and use it
    // for both the target state and the comparison model generation, so the merge always
    // runs against the most up-to-date form/attribute definitions.
    let snapshot: XRM_bcbs_groupconfigurationsnapshot | null = null;

    if (!snapshot)
        if (bcbs_groupconfigurationsnapshotid === state.data.snapshot.bcbs_groupconfigurationsnapshotid) {
            snapshot = structuredClone(state.data.snapshot);
        } else {
            snapshot = await appDataSource.getGroupConfigurationSnapshot(
                bcbs_groupconfigurationsnapshotid,
                undefined,
            );
        }

    // Snapshot - JSON:
    // ----------------------------------------
    const jsonFetch = snapshot.bcbs_groupconfigurationsnapshotid == state.data.snapshot.bcbs_groupconfigurationsnapshotid
        ? Promise.resolve({
            json: structuredClone(state.data.json)
        })
        : appDataSource.downloadGroupConfigurationSnapshot(snapshot)

    // Account:
    // ----------------------------------------
    let account = state.data.account;

    if (!account || account.accountid !== snapshot._bcbs_groupid_value)
        account = await appDataSource.getAccount(snapshot._bcbs_groupid_value!);

    // Release and Config:
    // ----------------------------------------
    // load the config release if it's not the same as the current snapshot
    let bcbs_config_release = state.data.config.bcbs_config_release;

    if (isMergeApp) {
        // When merging:
        // always load the latest published release, so the merge is always against the most up-to-date form/attribute definitions.
        bcbs_config_release = await appDataSource.bcbs_config_releases_byPublishedRelease_getAsync();
    } else {
        // if state is not the same as the current snapshot, load the config release for the snapshot 
        // else use the current config release in state
        if (snapshot._bcbs_releaseid_value != state.data.config.bcbs_config_release.bcbs_config_releaseid)
            bcbs_config_release = await appDataSource.getRelease(snapshot._bcbs_releaseid_value!)
    }

    // load the config json if it's not the same as the current snapshot
    let config = state.data.config;

    if (bcbs_config_release.bcbs_config_releaseid != state.data.config.bcbs_config_release.bcbs_config_releaseid) {
        config = await LoadConfigAsync(
            appDataSource,
            bcbs_config_release,
        );
    }

    if (!bcbs_config_release || !snapshot || !config)
        throw new Error('Failed to load snapshot or config release');

    // load the snapshot json if it's not the same as the current snapshot
    let jsonFetchResult = await jsonFetch;

    if (!jsonFetchResult)
        throw new Error('Snapshot not found');

    const compareState: IGCDataLight = {
        json: structuredClone(jsonFetchResult.json),
        snapshot: structuredClone(snapshot),
        config: config,
        account: structuredClone(account),
    };

    return compareState;
}

// Comment location helpers
// ============================================================================
export function getLastLocation(comment: IComment): ICommentRouteRef {
    let nextSegment = comment.location;

    // lets find valueRef, travers link list until the last node
    while (nextSegment.next != null) {
        nextSegment = nextSegment.next;
    }
    return nextSegment;
}
